

import links_common from "../core/relations/links_common";
import slackPrivate from "./critical_path/slack";
import critical_path from "./critical_path/critical_path";
// Phase B per `04-` §19 + D-28: rewire slack/CP methods behind the
// `_analysis_engine` flag. Default ("v1") preserves the v1 implementation
// below; users opt into "v2" via `gantt.config.auto_scheduling._analysis_engine`.
import { analyzeViaEngine, getSlackBetweenViaEngine, wireRevisionTracker } from "../scheduling/adapters/gantt_adapter";

// Returns true iff `gantt.config.auto_scheduling._analysis_engine === "v2"`.
// Defensive: if the auto_scheduling normalizer isn't loaded (critical-path-
// only plugin use), default to v1.
function _isAnalysisV2(gantt) {
	if (!gantt._getAutoSchedulingConfig) return false;
	try {
		var cfg = gantt._getAutoSchedulingConfig();
		return !!(cfg && cfg._analysis_engine === "v2");
	} catch (e) {
		return false;
	}
}

// Convert engine minutes to gantt's config duration unit. Phase B's
// approximation: build a time interval starting at the task's start_date
// (or a fixed anchor for synthetic computations) and ask gantt's calendar
// machinery to measure it in the target unit. This routes through the
// calendar so working-time semantics are preserved.
function _minutesToConfigUnit(gantt, task, minutes) {
	var unit = gantt.config.duration_unit;
	if (!unit || unit === "minute") return minutes;
	var base = (task && task.start_date) ? task.start_date : new Date(2026, 0, 1);
	var end = gantt.calculateEndDate({start_date: base, duration: minutes, unit:"minute", task: task});
	try {
		return gantt.calculateDuration({ start_date: base, end_date: end, unit: unit, task: task });
	} catch (e) {
		// Defensive fallback: simple ratio against minutes-per-unit.
		switch (unit) {
			case "hour": return minutes / 60;
			case "day":  return minutes / 1440;
			case "week": return minutes / (1440 * 7);
			default: return minutes;
		}
	}
}

function _v2_normalizeTaskId(id) {
	return String(id);
}


export default function(gantt){
	links_common(gantt);

// Phase B per D-20: wire the RevisionTracker so the v2 snapshot cache
// invalidates after gantt mutations. Idempotent.
wireRevisionTracker(gantt);

var _private = slackPrivate(gantt);
_private.init();

// v1 originals — preserved as the default branch under the _analysis_engine flag.
var _v1_getFreeSlack = function(task) {
	return _private.getFreeSlack(task);
};
var _v1_getTotalSlack = function(task) {
	return _private.getTotalSlack(task);
};

// v1 parity: `gantt.getTotalSlack()` (no arg) returns the internal slack
// map keyed by task id. The retest snippet at retest_migrations/critical_path
// "does not throw when calculating slack" exercises this path. v2 was
// dereferencing `task.id` on `undefined`; mirror v1 by returning a plain
// object aggregating every analyzed task's slack instead.
function _v2_slackMap(gantt, slackMinutesMap) {
	var out = {};
	slackMinutesMap.forEach(function(minutes, id) {
		if (!gantt.isTaskExists(id)) return;
		out[id] = _minutesToConfigUnit(gantt, gantt.getTask(id), minutes);
	});
	return out;
}

gantt.getFreeSlack = function(task) {
	if (_isAnalysisV2(gantt)) {
		var result = analyzeViaEngine(gantt);
		if (task === undefined) {
			return _v2_slackMap(gantt, result.freeSlack);
		}
		var minutes = result.freeSlack.get(_v2_normalizeTaskId(task.id));
		// Per D-15: cycle members are omitted; v1 returns undefined for
		// summary tasks with no computed children. Phase B returns 0 for
		// omitted to match `gantt.getTotalSlack(omittedTask) === 0` semantics
		// — documented as category-(a) behavior change in test-triage.md.
		if (minutes === undefined) return 0;
		return _minutesToConfigUnit(gantt, task, minutes);
	}
	return _v1_getFreeSlack(task);
};

gantt.getTotalSlack = function(task) {
	if (_isAnalysisV2(gantt)) {
		var result = analyzeViaEngine(gantt);
		if (task === undefined) {
			return _v2_slackMap(gantt, result.totalSlack);
		}
		var minutes = result.totalSlack.get(_v2_normalizeTaskId(task.id));
		if (minutes === undefined) return 0; // see comment above
		return _minutesToConfigUnit(gantt, task, minutes);
	}
	return _v1_getTotalSlack(task);
};

var criticalPath = critical_path(gantt);
gantt.config.highlight_critical_path = false;
criticalPath.init(_private);

var _v1_isCriticalTask = function (task) {
	gantt.assert(!!(task && task.id !== undefined), "Invalid argument for gantt.isCriticalTask");
	return criticalPath.isCriticalTask(task);
};

gantt.isCriticalTask = function (task) {
	gantt.assert(!!(task && task.id !== undefined), "Invalid argument for gantt.isCriticalTask");
	if (_isAnalysisV2(gantt)) {
		// Unscheduled and completed-under-use_progress tasks are NEVER critical
		// (v1 contract). Check these FIRST so the slack-based check below
		// can't override them.
		if (task.unscheduled) return false;
		var cfg = gantt._getAutoSchedulingConfig && gantt._getAutoSchedulingConfig();
		if (cfg && cfg.use_progress && task.progress === 1) return false;
		// Summary tasks: critical iff any descendant is critical (v1 quirk
		// from `ext/critical_path/critical_path.js`). Recurse into children
		// so the leaf threshold (below) governs both.
		if (gantt.isSummaryTask && gantt.isSummaryTask(task)) {
			var hasCritical = false;
			gantt.eachTask(function (child) {
				if (!hasCritical && gantt.isCriticalTask(child)) hasCritical = true;
			}, task.id);
			return hasCritical;
		}
		var result = analyzeViaEngine(gantt);
		return result.isCritical.get(_v2_normalizeTaskId(task.id)) === true;
	}
	return _v1_isCriticalTask(task);
};

gantt.isCriticalLink = function (link) {
	// v1 derivation preserved: critical link = source task critical.
	// In v2 the source-task lookup goes through the v2 isCriticalTask above,
	// which routes correctly per `_analysis_engine`.
	return this.isCriticalTask(gantt.getTask(link.source));
};


gantt.getSlack = function(task1, task2) {
	if (_isAnalysisV2(gantt)) {
		var minutes = getSlackBetweenViaEngine(gantt, task1.id, task2.id);
		// Engine returns Infinity when no simple path connects (t1, t2) in
		// either direction. Per Phase B note (TR-002) preserve v1's no-path
		// date-arithmetic semantics by falling back to the v1 body — engine
		// owns the "connected pair" case only; v1 owns the disconnected case.
		if (!isFinite(minutes)) {
			return _v1_getSlack_body.call(gantt, task1, task2);
		}
		// Slack in source-task working minutes per `04-` §17.2; convert to
		// gantt's config duration_unit at the boundary.
		return _minutesToConfigUnit(gantt, task1, minutes);
	}
	return _v1_getSlack_body.call(gantt, task1, task2);
};

// v1 getSlack body extracted so both branches can reuse it for now.
function _v1_getSlack_body(task1, task2) {
	var minSlack = 0;
	var relations = [];
	var common = {};

	for (var i = 0; i < task1.$source.length; i++) {
		common[task1.$source[i]] = true;
	}
	for (var i = 0; i < task2.$target.length; i++) {
		if(common[task2.$target[i]])
			relations.push(task2.$target[i]);
	}
	// there is at least one link
	if (relations[0]){
		for (var i = 0; i < relations.length; i++) {
			var link = this.getLink(relations[i]);
			var newSlack = this._getSlack(task1, task2, this._convertToFinishToStartLink(link.id, link, task1, task2, task1.parent, task2.parent));

			if (minSlack > newSlack || i === 0) {
				minSlack = newSlack;
			}
		}
	}
	else { // there are no links, but we still need the slack (for the children of a project task)
		minSlack = this._getSlack(task1, task2, {});
	}
	return minSlack;
}

gantt._getSlack = function (task, next_task, relation) {
	// relation - link expressed as finish-to-start (gantt._convertToFinishToStartLink)
	var types = this.config.types;

	var from = null;
	if (this.getTaskType(task.type) == types.milestone) {
		from = task.start_date;
	} else {
		from = task.end_date;
	}

	var to = next_task.start_date;

	var duration = 0;
	if (+from > +to) {
		duration = -this.calculateDuration({start_date: to, end_date: from, task: task});
	} else {
		duration = this.calculateDuration({start_date: from, end_date: to, task: task});
	}

	var lag = relation.lag;
	if (lag && lag*1 == lag) {
		duration -= lag;
	}

	return duration;
};


};
